import { utils } from '../../tools/Utils.js';

const ICONS = {
    open: "new-window",
    replace: "search",
    convert: "paragraph",
    clear: "close",
};
/**
 * Панель состояния и действий режима `mixed`.
 *
 * 2026-08-14: создаётся соседним элементом ПОСЛЕ поля (или после `.im-combo-box-toggle`),
 * а не обёрткой вокруг него. Так не ломаются существующие селекторы и разметка
 * вокруг `input`, и не требуется ничего от бэкенда.
 *
 * Владелец панели — виджет: он же её синхронизирует и удаляет.
 */
class MixedActionsBar {
    constructor(host, options) {
        this._buttons = new Map();
        this._destroyed = false;
        this._onClick = (event) => {
            const target = event.target?.closest("[data-mixed-action]");
            if (!target || !this._root.contains(target))
                return;
            event.preventDefault();
            switch (target.dataset.mixedAction) {
                case "open":
                    this._opts.onOpen();
                    break;
                case "replace":
                    this._opts.onReplace();
                    break;
                case "convert":
                    this._opts.onConvertToText();
                    break;
                case "clear":
                    this._opts.onClear();
                    break;
            }
        };
        this._opts = options;
        this._root = document.createElement("div");
        this._root.className = "im-autocomplete-actions";
        this._root.hidden = true;
        this._badge = document.createElement("span");
        this._badge.className = "im-autocomplete-badge";
        this._root.appendChild(this._badge);
        const ids = options.canOpen
            ? ["open", "replace", "convert", "clear"]
            : ["replace", "convert", "clear"];
        for (const id of ids) {
            const button = this._createButton(id);
            this._buttons.set(id, button);
            this._root.appendChild(button);
        }
        // Встаём после toggle-кнопки, если она есть, чтобы её не подвинуть
        const next = host.nextElementSibling;
        const anchor = next?.classList.contains("im-combo-box-toggle") ? next : host;
        anchor.insertAdjacentElement("afterend", this._root);
        this._root.addEventListener("click", this._onClick);
    }
    /**
     * Привести панель в соответствие текущему состоянию значения.
     *
     * 2026-08-16: панель видна всегда, включая пустое состояние — «заменить»
     * открывает список справочника и нужна до того, как что-либо выбрано.
     * Остальные действия появляются, когда к ним есть что применить.
     */
    render(state) {
        if (this._destroyed)
            return;
        this._root.hidden = false;
        const isEmpty = state.type === "empty";
        const isReference = state.type === "reference";
        // Признак «запись справочника» и подпись при неизвестном названии
        this._badge.hidden = !isReference;
        if (isReference) {
            const unresolved = state.displayName === null;
            this._badge.classList.toggle("is-unresolved", unresolved);
            this._badge.textContent = unresolved
                ? this._opts.labels.referenceNameMissing
                : this._opts.labels.referenceBadge;
            this._badge.title = unresolved ? state.id : "";
        }
        // «Открыть карточку» и «сделать текстом» осмысленны только для ссылки,
        // «очистить» — когда есть что очищать
        const open = this._buttons.get("open");
        if (open)
            open.hidden = !isReference;
        const convert = this._buttons.get("convert");
        if (convert)
            convert.hidden = !isReference;
        const clear = this._buttons.get("clear");
        if (clear)
            clear.hidden = isEmpty;
        // Пока значения нет, «заменить» не по смыслу — та же кнопка выбирает запись
        const replace = this._buttons.get("replace");
        if (replace) {
            this._setTitle(replace, isEmpty ? this._opts.labels.selectRecord : this._opts.labels.replace);
        }
    }
    /** Удалить панель и снять слушатели (идемпотентно). */
    destroy() {
        if (this._destroyed)
            return;
        this._destroyed = true;
        this._root.removeEventListener("click", this._onClick);
        this._root.remove();
        this._buttons.clear();
    }
    _createButton(id) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "im-autocomplete-action btn-unset";
        button.dataset.mixedAction = id;
        // Для replace подпись зависит от состояния и уточняется в render
        const title = id === "open" ? this._opts.labels.openCard
            : id === "replace" ? this._opts.labels.selectRecord
                : id === "convert" ? this._opts.labels.convertToText
                    : this._opts.labels.clear;
        this._setTitle(button, title);
        button.innerHTML = utils.createSvg(ICONS[id]);
        // Иконка декоративная: доступное имя даёт aria-label кнопки
        button.firstElementChild?.setAttribute("aria-hidden", "true");
        return button;
    }
    /** Подпись кнопки: подсказка и доступное имя задаются вместе. */
    _setTitle(button, title) {
        button.title = title;
        button.setAttribute("aria-label", title);
    }
}

export { MixedActionsBar };
